import { useMemo, useState } from "react";
import { Undo2 } from "lucide-react";
import { ApiV2Error } from "../../lib/api-v2";
import { cn } from "../../lib/utils";
import { formatWhen } from "../orders/format-when";
import type { RefundRequestStatus } from "../orders/types";
import { credits, rupees } from "../products/types";
import { useRefundRequests } from "./queries";
import { RefundReviewModal } from "./refund-review-modal";
import { RefundStatusBadge } from "./refund-status-badge";

const errorMessage = (err: unknown, fallback: string) => (err instanceof ApiV2Error ? err.message : fallback);

const FILTERS: { value: RefundRequestStatus | null; label: string }[] = [
  { value: "requested", label: "To review" },
  { value: "processing", label: "In progress" },
  { value: "refunded", label: "Refunded" },
  { value: "rejected", label: "Rejected" },
  { value: "failed", label: "Failed" },
  { value: null, label: "All" },
];

/**
 * /admin/refund-requests: accounts asking for the unused credits of a paid order to be
 * refunded. Opening one shows what approving would refund now.
 */
export function AdminRefundRequestsPage() {
  const [status, setStatus] = useState<RefundRequestStatus | null>("requested");
  const [openId, setOpenId] = useState<string | null>(null);
  const requests = useRefundRequests(status);
  const all = useMemo(() => requests.data?.pages.flatMap((p) => p.items) ?? [], [requests.data]);

  return (
    <div className="space-y-6 p-8">
      <div>
        <h1 className="text-3xl font-bold text-ink">Refund Requests</h1>
        <p className="mt-1 text-sm text-ink-2">
          Only credits still unused are refunded, at the order's rate plus GST. Approving takes them back from the
          account and refunds through Razorpay.
        </p>
      </div>

      <div className="rounded-xl border border-line bg-elevated shadow-sm">
        <div className="flex flex-wrap gap-2 border-b border-line p-4" role="tablist" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <button
              key={f.label}
              role="tab"
              aria-selected={status === f.value}
              onClick={() => setStatus(f.value)}
              className={cn(
                "rounded-lg px-3 py-1.5 text-sm font-semibold",
                status === f.value ? "bg-brand-soft text-brand" : "text-ink-2 hover:bg-soft",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {requests.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-soft" />)}
          </div>
        ) : requests.isError ? (
          <div className="flex flex-col items-center gap-3 p-10 text-center">
            <p className="text-sm text-danger">{errorMessage(requests.error, "Could not load refund requests.")}</p>
            <button onClick={() => { void requests.refetch(); }} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">
              Retry
            </button>
          </div>
        ) : all.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center">
            <span className="rounded-full bg-soft p-3 text-ink-3"><Undo2 className="h-5 w-5" /></span>
            <p className="font-semibold text-ink">No refund requests here</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-wider text-ink-3">
                <th className="px-4 py-3 font-semibold">Requested</th>
                <th className="px-4 py-3 font-semibold">Order</th>
                <th className="px-4 py-3 font-semibold">Account</th>
                <th className="px-4 py-3 font-semibold">Reason</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Refund</th>
              </tr>
            </thead>
            <tbody>
              {all.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setOpenId(r.id)}
                  aria-label={`Refund request ${r.reference_number}`}
                  className="cursor-pointer border-b border-line last:border-0 hover:bg-soft/50"
                >
                  <td className="whitespace-nowrap px-4 py-3 text-ink-2">{formatWhen(r.created_at)}</td>
                  <td className="px-4 py-3 font-mono font-semibold text-ink">{r.reference_number}</td>
                  <td className="max-w-[12rem] truncate px-4 py-3 text-ink-2" title={r.client_id}>
                    {r.requested_by_email ?? r.client_id}
                  </td>
                  <td className="max-w-xs truncate px-4 py-3 text-ink" title={r.reason}>{r.reason}</td>
                  <td className="px-4 py-3"><RefundStatusBadge status={r.status} /></td>
                  <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-ink">
                    {r.total_amount !== null && r.credits !== null ? (
                      <>
                        {rupees(r.total_amount)}
                        <span className="block text-xs text-ink-3">{credits(r.credits)} credits</span>
                      </>
                    ) : (
                      <span className="text-ink-3">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {requests.hasNextPage && (
          <div className="border-t border-line p-3 text-center">
            <button
              onClick={() => { void requests.fetchNextPage(); }}
              disabled={requests.isFetchingNextPage}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-brand hover:bg-brand-soft disabled:cursor-wait disabled:text-ink-3"
            >
              {requests.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>

      {openId && <RefundReviewModal requestId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
